import { Combobox } from "@headlessui/react";
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { ISelectDropdownItem } from "./SelectDropdown";
import _ from "lodash";
import clsx from "clsx";
import { FaTimesCircle } from "react-icons/fa";
import { ScrollToFormikError } from "../elements/ScrollToFormikError";
import { ChevronDownIcon } from "lucide-react";

interface IProps {
    name: string;
    list: Array<ISelectDropdownItem>;
    listSelectedValue?: number | string | null;
    onChange: Dispatch<SetStateAction<ISelectDropdownItem | null>> | any;
    disabled?: boolean;
    placeholder?: string;
    onSearchChange?: (query: string) => void;
    label?: string;
    required?: boolean;
    error?: string;
    helperText?: string;
    description?: string;
}

export default function InputSelectDropdown(props: IProps) {
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState<ISelectDropdownItem | undefined>(props.list.find((listItem: ISelectDropdownItem) => listItem.value === props.listSelectedValue));

    useEffect(() => {
        if (!selected) {
            setSelected(props.list.find((listItem: ISelectDropdownItem) => listItem.value === props.listSelectedValue));
        }
    }, [props.list])

    const fieldRef = useRef<HTMLDivElement>(null);

    const [searchResult, setSearchResult] = useState<Array<ISelectDropdownItem>>([]);
    const searchedList =
        _.isEmpty(searchResult) && query.length < 1 ? props.list : searchResult;

    useEffect(() => {
        filterList();
    }, [query, props.list])

    const onSearchChange = (evt: ChangeEvent<HTMLInputElement> | undefined) => {
        const val = evt?.target.value ?? '';
        props.onSearchChange?.(val);

        setQuery(val);
    };

    const getListItemDisplayName = () => {
        return props.list.find((listItem: ISelectDropdownItem) => listItem.value === props.listSelectedValue)?.name
    }

    const filterList = () => {
        const srchList: Array<ISelectDropdownItem> = [];

        props.list.forEach((listItem) => {
            if (typeof listItem.name === "string") {
                if (listItem.name?.toString().toLowerCase().includes(query)) srchList.push(listItem);
            } else {
                srchList.push(listItem);
            }
        });
        setSearchResult(srchList);
    }

    return (
        <Combobox
            ref={fieldRef}
            value={selected?.value}
            onChange={(item: any) => {
                props.onChange(item.value);
                setSelected(item)
            }}
            disabled={props.disabled}
            as={"div"}
        >
            {/* Label */}
            {props.label && (
                <Combobox.Label
                    className={clsx('block text-sm font-medium text-gray-700 dark:text-text-dark-primary', {
                        ['!text-danger-main']: props.error && props.required
                    })}>
                    {props.label} <span className="!text-danger-main">{props.required && '*'}</span>
                    {props.description && (
                        <p
                            className={clsx('text-xs text-gray-400 dark:text-text-dark-secondary italic', {
                                ['!text-danger-border']: props.error && props.required
                            })}>
                            {props.description}
                        </p>
                    )}
                </Combobox.Label>
            )}

            <div className="relative">
                {selected ? (
                    <div className="w-full rounded-lg border border-gray-300 dark:border-primary-dark-border_active px-2 py-2 flex justify-between items-center">
                        {selected.name}
                        {!props.disabled && (
                            <FaTimesCircle
                                className="w-7 text-danger-main mr-2 cursor-pointer"
                                title="Clear selected"
                                onClick={() => {
                                    setSelected(undefined);
                                    props.onChange("");
                                }}
                            />
                        )}
                    </div>
                ) : <div className="relative">
                    <Combobox.Input
                            className="w-full rounded-lg border border-gray-300 dark:border-primary-dark-border_active px-2 py-2 dark:bg-primary-dark-main dark:text-text-dark-primary"
                        displayValue={() => query}
                        onChange={(e) => onSearchChange(e)}
                        placeholder={props.placeholder ?? "Search..."}
                    />

                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-2">
                        <ChevronDownIcon className={clsx("h-5 w-5 text-gray-400 dark:text-text-dark-primary")} aria-hidden="true" />
                    </Combobox.Button>
                </div>

                }

                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border dark:border-primary-dark-border_active bg-white dark:bg-primary-dark-main shadow-lg">
                    {searchedList.map((item: ISelectDropdownItem) => (
                        <Combobox.Option
                            key={item.id}
                            value={item}
                            className={({ active }) =>
                                `cursor-pointer px-4 py-2 ${active ? "bg-green-50 dark:bg-primary-dark-secondary" : ""
                                }`
                            }
                        >
                            {item.name}
                        </Combobox.Option>
                    ))}
                </Combobox.Options>
            </div>
            <ScrollToFormikError
                fieldName={props.name ?? ""}
                fieldRef={fieldRef}
            />
        </Combobox>
    );
}
